/**
 * ProfessionalDevelopmentHub Component
 *
 * Purpose:
 * - Allow directors to configure external training resource URLs for required professional development categories.
 * - Staff can click "Visit" to open the training portal in a new tab.
 * - Supports the annual 12 clock hour minimum professional development requirement.
 * - Director inputs their own URLs — not state-specific.
 */

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  ExternalLink, Settings, RefreshCw, BookOpen, HeartPulse,
  AlertTriangle, Apple, Accessibility, Plus, GraduationCap,
  Upload, Download, Printer, CheckCircle2, Clock, Edit,
  ChevronDown, ChevronUp, UserCheck, CalendarDays, Send,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRAINING_CATEGORIES = [
  {
    key: "childDevelopmentUrl",
    title: "Child Development & Learning",
    description: "Training on child development milestones, learning strategies, age-appropriate activities, and curriculum planning.",
    icon: GraduationCap,
    iconBg: "bg-emerald-500/30",
    iconColor: "text-emerald-200",
  },
  {
    key: "healthSafetyUrl",
    title: "Fire Safety Training",
    description: "Fire drill procedures, evacuation plans, fire extinguisher training, and emergency response protocols for childcare facilities.",
    icon: HeartPulse,
    iconBg: "bg-red-500/30",
    iconColor: "text-red-200",
  },
  {
    key: "mandatedReporterUrl",
    title: "Mandated Reporter Training",
    description: "Child abuse and neglect recognition, reporting obligations, and documentation procedures for all childcare staff.",
    icon: AlertTriangle,
    iconBg: "bg-yellow-500/30",
    iconColor: "text-yellow-200",
  },
  {
    key: "healthBasicsUrl",
    title: "Health & Safety Basics",
    description: "CPR, First Aid, medication administration, illness management, and general health and safety protocols for childcare settings.",
    icon: HeartPulse,
    iconBg: "bg-blue-500/30",
    iconColor: "text-blue-200",
  },
];

const DEFAULT_SETTINGS = {
  childDevelopmentUrl: "",
  healthSafetyUrl: "",
  mandatedReporterUrl: "",
  healthBasicsUrl: "",
  custom1Label: "", custom1Url: "",
  custom2Label: "", custom2Url: "",
  custom3Label: "", custom3Url: "",
  custom4Label: "", custom4Url: "",
  custom5Label: "", custom5Url: "",
  custom6Label: "", custom6Url: "",
};

export const ProfessionalDevelopmentHub = ({ isOwner }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/professional-development-hub/settings`);
      if (res.data) setSettings({ ...DEFAULT_SETTINGS, ...res.data });
    } catch {
      // use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/professional-development-hub/settings`, settings);
      toast.success("Professional Development Hub settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const isValidUrl = (str) => {
    try { new URL(str); return true; } catch { return false; }
  };

  const openUrl = (url) => {
    if (url && isValidUrl(url)) window.open(url, "_blank", "noopener,noreferrer");
  };

  const set = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  // ── Tracker state ──
  const [myLog, setMyLog] = useState(null);
  const [directorOverview, setDirectorOverview] = useState(null);
  const [periodSettings, setPeriodSettings] = useState({ startMonth: 1 });
  const [savingPeriod, setSavingPeriod] = useState(false);
  const [showHistory, setShowHistory] = useState({});
  const [reminderOpen, setReminderOpen] = useState(null);
  const [reminderChannels, setReminderChannels] = useState({ email: false, sms: false });
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderConfirmStaff, setReminderConfirmStaff] = useState(null);

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  useEffect(() => {
    fetchMyLog();
    fetchPeriodSettings();
    if (isOwner) { fetchDirectorOverview(); }
  }, [isOwner]);

  const fetchMyLog = async () => {
    try {
      const res = await axios.get(`${API}/prof-dev-tracker/my-log`);
      setMyLog(res.data);
    } catch {}
  };

  const fetchPeriodSettings = async () => {
    try {
      const res = await axios.get(`${API}/prof-dev-tracker/settings`);
      setPeriodSettings(res.data);
    } catch {}
  };

  const fetchDirectorOverview = async () => {
    try {
      const res = await axios.get(`${API}/prof-dev-tracker/director/overview`);
      setDirectorOverview(res.data);
    } catch {}
  };

  const handleViewCert = (entryId) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/prof-dev-tracker/log/${entryId}/view?token=${token}`, '_blank');
  };

  const handleDownloadCert = (entryId, fileName) => {
    const token = localStorage.getItem('token');
    const a = document.createElement('a');
    a.href = `${API}/prof-dev-tracker/log/${entryId}/download?token=${encodeURIComponent(token)}`;
    a.download = fileName || 'certificate';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleSavePeriod = async () => {
    setSavingPeriod(true);
    try {
      await axios.patch(`${API}/prof-dev-tracker/settings`, { startMonth: periodSettings.startMonth });
      toast.success("Compliance period saved");
      fetchMyLog();
      if (isOwner) fetchDirectorOverview();
    } catch { toast.error("Failed to save period"); }
    finally { setSavingPeriod(false); }
  };

  const hoursBar = (current, required) => {
    const pct = Math.min(100, (current / required) * 100);
    const color = pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-400' : 'bg-red-400';
    return (
      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
        <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
        <span className="ml-2 text-gray-600">Loading Professional Development Hub...</span>
      </div>
    );
  }

  const daysUntilPeriodEnd = directorOverview
    ? Math.ceil((new Date(directorOverview.periodEnd + 'T12:00:00') - new Date()) / 86400000)
    : null;
  const NEARING_WINDOW_DAYS = 60;
  const isNearingEnd = daysUntilPeriodEnd !== null && daysUntilPeriodEnd <= NEARING_WINDOW_DAYS && daysUntilPeriodEnd >= 0;
  const behindStaff = (directorOverview?.staff || []).filter(s => !s.isCompliant && s.totalHours < s.requiredHours);

  const handleSendReminder = async (staff) => {
    const channels = Object.entries(reminderChannels).filter(([, v]) => v).map(([k]) => k);
    if (channels.length === 0) {
      toast.error("Select Email and/or SMS first");
      return;
    }
    setReminderSending(true);
    try {
      const res = await axios.post(`${API}/prof-dev-tracker/send-reminder/${staff.staffId}`, {
        channels
      });
      const results = res.data?.results || {};
      const parts = [];
      if (results.email === 'sent') parts.push(`Emailed ${staff.staffName}`);
      if (results.email === 'no_email_on_file') parts.push(`No email on file for ${staff.staffName}`);
      if (results.email === 'failed') parts.push(`Email to ${staff.staffName} failed`);
      if (results.sms === 'sent') parts.push(`Texted ${staff.staffName}`);
      if (results.sms === 'no_phone_on_file') parts.push(`No phone on file for ${staff.staffName}`);
      if (results.sms === 'failed') parts.push(`SMS to ${staff.staffName} failed`);
      const anySent = results.email === 'sent' || results.sms === 'sent';
      if (anySent) toast.success(parts.join(' · ')); else toast.error(parts.join(' · ') || "Nothing sent");
      setReminderOpen(null);
      setReminderChannels({ email: false, sms: false });
      setReminderConfirmStaff(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
      setReminderConfirmStaff(null);
    } finally {
      setReminderSending(false);
    }
  };


  const printTrainingSummary = (staffArr) => {
    if (!directorOverview) return;
    const fmt = (d) => new Date(d + 'T12:00:00').toLocaleDateString();
    const periodLabel = `${fmt(directorOverview.periodStart)} — ${fmt(directorOverview.periodEnd)}`;
    const typeLabel = { auto_clearance: 'Clearance', self: 'Self-logged', director_credit: 'Director credit', director_entry: 'Staff record' };
    const sections = staffArr.map(s => {
      const status = s.isCompliant
        ? '<span style="color:#15803d;font-weight:700">COMPLIANT</span>'
        : `<span style="color:#b45309;font-weight:700">BEHIND — needs ${Math.max(0, (s.requiredHours - s.totalHours)).toFixed(1)} more hrs</span>`;
      const rows = (s.entries || []).map(e => `<tr>
        <td>${(e.trainingName || '').replace(/</g,'&lt;')}</td>
        <td style="text-align:center">${e.hours}</td>
        <td style="text-align:center">${e.completionDate ? fmt(e.completionDate) : ''}</td>
        <td style="text-align:center">${typeLabel[e.entryType] || e.entryType || ''}</td>
      </tr>`).join('') || '<tr><td colspan="4" style="text-align:center;color:#888;font-style:italic">No training recorded this period</td></tr>';
      return `<div class="staff">
        <h2>${(s.staffName || '').replace(/</g,'&lt;')}${s.role ? ` <span class="role">(${s.role})</span>` : ''}</h2>
        <p class="hrs">Completed <b>${s.totalHours}</b> of <b>${s.requiredHours}</b> required hours &nbsp;·&nbsp; ${status}</p>
        <table><thead><tr><th>Training</th><th>Hours</th><th>Date</th><th>Type</th></tr></thead><tbody>${rows}</tbody></table>
      </div>`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Annual Professional Development Summary</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1f2937;margin:32px}
        h1{font-size:20px;margin:0 0 2px}
        .sub{color:#6b7280;font-size:12px;margin:0 0 18px}
        .staff{page-break-inside:avoid;margin:0 0 22px;padding:0 0 14px;border-bottom:1px solid #e5e7eb}
        .staff h2{font-size:15px;margin:0 0 3px}
        .role{color:#6b7280;font-weight:400;font-size:12px}
        .hrs{font-size:12px;margin:0 0 8px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #e5e7eb;padding:5px 8px}
        th{background:#f3f4f6;text-align:left}
        @media print{button{display:none}}
      </style></head><body>
      <h1>Annual Professional Development Summary</h1>
      <p class="sub">Compliance period: ${periodLabel} &nbsp;·&nbsp; Generated ${new Date().toLocaleDateString()}</p>
      ${sections}
      <button onclick="window.print()" style="margin-top:12px;padding:8px 16px;font-size:14px;cursor:pointer">Print</button>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Please allow pop-ups to print the summary'); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (_) {} }, 400);
  };


  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-testid="profdev-hub-hero">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("https://customer-assets.emergentagent.com/job_4e8e2bca-21e7-4a27-ac90-500c1c6fe36a/artifacts/uz3j5f53_image.png")' }}
        />
        <div className="absolute inset-0 bg-black/40" />

        <div className="relative z-10 p-8 md:p-12" style={{WebkitFontSmoothing:'antialiased',MozOsxFontSmoothing:'grayscale'}}>
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-emerald-900/70 px-4 py-2 rounded-full mb-6 border border-white/30">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
              <span className="text-white text-sm font-medium tracking-wide" style={{WebkitFontSmoothing:'antialiased'}}>Annual Training Resources</span>
            </div>

            {/* Title row */}
            <div className="flex items-start gap-4 mb-4">
              <div className="p-4 bg-emerald-900/70 rounded-2xl shadow-lg border border-white/20">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-white mb-1" style={{WebkitFontSmoothing:'antialiased',MozOsxFontSmoothing:'grayscale'}}>
                  Professional Development Hub
                </h1>
                <p className="text-white text-lg font-normal" style={{WebkitFontSmoothing:'antialiased',MozOsxFontSmoothing:'grayscale'}}>
                  Central access to all required annual training resources
                </p>
              </div>
            </div>

            {/* Requirement Banner */}
            <div className="bg-white/15 border border-white/30 rounded-xl px-5 py-3 mb-8 flex items-center gap-3">
              <GraduationCap className="w-6 h-6 text-yellow-300 flex-shrink-0" />
              <p className="text-white text-sm font-normal" style={{WebkitFontSmoothing:'antialiased',MozOsxFontSmoothing:'grayscale'}}>
                All childcare staff are required to complete a minimum of <span className="text-yellow-300 font-semibold">12 clock hours</span> of ongoing annual professional development training. Directors must maintain verification of completion.
              </p>
            </div>

            <p className="text-white text-base mb-8 max-w-2xl font-normal" style={{WebkitFontSmoothing:'antialiased',MozOsxFontSmoothing:'grayscale'}}>
              {isOwner
                ? "Configure the URLs below for each training category. Staff will see direct links to access these resources from this page."
                : "Click any button below to access your required annual training resources and complete your 12 clock hour requirement."}
            </p>

            {/* Training Category Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {TRAINING_CATEGORIES.map(({ key, title, description, icon: Icon, iconBg, iconColor }) => (
                <div
                  key={key}
                  className="bg-emerald-900/75 rounded-2xl p-5 border border-white/20 space-y-3 shadow-lg"
                  data-testid={`profdev-card-${key}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${iconBg} rounded-lg flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${iconColor}`} />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm" style={{WebkitFontSmoothing:'antialiased',MozOsxFontSmoothing:'grayscale'}}>{title}</p>
                      <p className="text-white/80 text-xs font-normal" style={{WebkitFontSmoothing:'antialiased',MozOsxFontSmoothing:'grayscale'}}>{description}</p>
                    </div>
                  </div>

                  {isOwner ? (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        type="url"
                        value={settings[key]}
                        onChange={(e) => set(key, e.target.value)}
                        placeholder="https://..."
                        className="flex-1 h-10 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:bg-white/20 text-sm rounded-xl"
                        data-testid={`input-${key}`}
                      />
                      <Button
                        onClick={() => openUrl(settings[key])}
                        disabled={!settings[key] || !isValidUrl(settings[key])}
                        size="sm"
                        className="h-10 px-4 bg-white text-emerald-700 hover:bg-white/90 font-bold rounded-xl shadow transition-all hover:scale-105"
                        data-testid={`open-${key}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Visit
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => openUrl(settings[key])}
                      disabled={!settings[key] || !isValidUrl(settings[key])}
                      className="w-full h-10 bg-white text-emerald-700 hover:bg-white/90 font-bold rounded-xl shadow transition-all hover:scale-105"
                      data-testid={`open-${key}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {settings[key] && isValidUrl(settings[key]) ? "Access Training" : "URL Not Configured"}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Custom Training Slots — 6 total */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1,2,3,4,5,6].map((num) => {
                const labelKey = `custom${num}Label`;
                const urlKey = `custom${num}Url`;
                return (
                <div
                  key={urlKey}
                  className="bg-emerald-900/75 rounded-2xl p-5 border border-white/20 border-dashed space-y-3 shadow-lg"
                  data-testid={`profdev-card-custom${num}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/15 rounded-lg">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-white font-semibold text-sm" style={{WebkitFontSmoothing:'antialiased',MozOsxFontSmoothing:'grayscale'}}>Additional Training Resource {num}</p>
                  </div>

                  {isOwner ? (
                    <div className="space-y-2">
                      <Input
                        type="text"
                        value={settings[labelKey] || ""}
                        onChange={(e) => set(labelKey, e.target.value)}
                        placeholder="Training Name"
                        className="h-9 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:bg-white/20 text-sm rounded-xl font-medium"
                        data-testid={`input-${labelKey}`}
                      />
                      <div className="flex gap-2">
                        <Input
                          type="url"
                          value={settings[urlKey] || ""}
                          onChange={(e) => set(urlKey, e.target.value)}
                          placeholder="https://..."
                          className="flex-1 h-10 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:bg-white/20 text-sm rounded-xl"
                          data-testid={`input-${urlKey}`}
                        />
                        <Button
                          onClick={() => openUrl(settings[urlKey])}
                          disabled={!settings[urlKey] || !isValidUrl(settings[urlKey])}
                          size="sm"
                          className="h-10 px-4 bg-white text-emerald-700 hover:bg-white/90 font-bold rounded-xl shadow transition-all hover:scale-105"
                          data-testid={`open-${urlKey}`}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />Visit
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {settings[labelKey] && (
                        <p className="text-white/90 font-medium text-sm">{settings[labelKey]}</p>
                      )}
                      <Button
                        onClick={() => openUrl(settings[urlKey])}
                        disabled={!settings[urlKey] || !isValidUrl(settings[urlKey])}
                        className="w-full h-10 bg-white text-emerald-700 hover:bg-white/90 font-bold rounded-xl shadow transition-all hover:scale-105"
                        data-testid={`open-${urlKey}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {settings[urlKey] && isValidUrl(settings[urlKey])
                          ? settings[labelKey] || "Access Training"
                          : "URL Not Configured"}
                      </Button>
                    </div>
                  )}
                </div>
              );})}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button (owner only) */}
      {isOwner && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white px-8 py-3 text-base font-semibold shadow-lg rounded-xl"
            data-testid="profdev-hub-save-btn"
          >
            <Settings className="w-5 h-5 mr-2" />
            {saving ? "Saving..." : "Save Professional Development Settings"}
          </Button>
        </div>
      )}

      {/* ── Inspection Month Setting (owner only) ── */}
      {isOwner && (
        <div className="bg-white rounded-2xl border-2 border-emerald-200 p-5 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-emerald-600" />
            Inspection Month Setting
          </h3>
          <p className="text-xs text-gray-500 mb-3">Set the month your inspection month begins. Professional Development hours are synced and counted from this month to the same month next year.</p>
          <div className="flex items-center gap-3 flex-wrap">
            <select value={periodSettings.startMonth}
              onChange={e => setPeriodSettings(p => ({...p, startMonth: parseInt(e.target.value)}))}
              className="border border-emerald-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400">
              {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <Button onClick={handleSavePeriod} disabled={savingPeriod} size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">
              {savingPeriod ? "Saving..." : "Save Period"}
            </Button>
            {directorOverview && (
              <span className="text-xs text-gray-500 italic">
                Current period: {new Date(directorOverview.periodStart + 'T12:00:00').toLocaleDateString('en-US', {month:'short',year:'numeric'})} — {new Date(directorOverview.periodEnd + 'T12:00:00').toLocaleDateString('en-US', {month:'short',year:'numeric'})}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── My Training Log ── */}
      <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <BookOpen className="w-5 h-5" /> My Training Log
              </h3>
              <p className="text-emerald-100 text-xs mt-0.5">Read-only summary, synced automatically from Edit Staff</p>
            </div>
            {myLog && (
              <div className={`px-4 py-2 rounded-xl text-sm font-bold border ${myLog.isCompliant ? 'bg-green-100 border-green-400 text-green-800' : 'bg-white/20 border-white/40 text-white'}`}>
                {myLog.isCompliant
                  ? <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Compliant</span>
                  : <span>{myLog.currentPeriodHours} / {myLog.requiredHours} hrs</span>}
              </div>
            )}
          </div>
          {myLog && !myLog.isCompliant && (
            <div className="mt-2">{hoursBar(myLog.currentPeriodHours, myLog.requiredHours)}</div>
          )}
        </div>
        <div className="p-5 space-y-4">
          {/* Info note — always visible inside the card body */}
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800">
            <span className="mt-0.5 text-blue-500 flex-shrink-0">ℹ</span>
            <span>
              Professional Development is automatically synced from your Staff Record. To add training hours or upload a certificate, go to <strong>Staff</strong> → <strong className="text-blue-800">Edit Staff</strong> <Edit className="inline w-3 h-3 mx-0.5 text-blue-600" /> → <strong>Prof Dev (12 Hours)</strong>. Your progress will update here automatically.
            </span>
          </div>

          {myLog?.entries?.length > 0 ? (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {myLog.entries.map(entry => (
                <div key={entry.id}
                  className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border ${entry.inCurrentPeriod ? 'bg-white border-emerald-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{entry.trainingName}
                      {entry.entryType === 'director_credit' && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Director Entry</span>}
                      {entry.entryType === 'director_entry' && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Staff Record</span>}
                      {!entry.inCurrentPeriod && <span className="ml-2 text-xs text-gray-400 italic">Prior period</span>}
                    </p>
                    <p className="text-xs text-gray-500">{entry.hours} hrs · {new Date(entry.completionDate + 'T12:00:00').toLocaleDateString()}{entry.notes ? ` · ${entry.notes}` : ''}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {entry.fileName && (<>
                      <button onClick={() => handleViewCert(entry.id)} title="View" className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><Printer className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDownloadCert(entry.id, entry.fileName)} title="Download" className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"><Download className="w-3.5 h-3.5" /></button>
                    </>)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic text-center py-4">No additional training entries yet — log workshops, conferences, or other training above.</p>
          )}
        </div>
      </div>

      {/* ── Director Overview ── */}
      {isOwner && directorOverview && (
        <div className="bg-white rounded-2xl border-2 border-blue-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-700 to-indigo-700 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-white font-bold text-lg flex items-center gap-2"><UserCheck className="w-5 h-5" /> Staff Training Progress</h3>
              <p className="text-blue-200 text-xs mt-0.5">Automatically synced from each staff member's Edit Staff record · Period: {new Date(directorOverview.periodStart + 'T12:00:00').toLocaleDateString()} — {new Date(directorOverview.periodEnd + 'T12:00:00').toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => printTrainingSummary(directorOverview.staff)} size="sm" variant="outline" className="bg-white/10 text-white border-white/40 hover:bg-white/20 font-semibold rounded-xl" data-testid="print-all-summary-btn">
                <Printer className="w-4 h-4 mr-1" /> Print All
              </Button>
            </div>
          </div>

          {isNearingEnd && behindStaff.length > 0 && (
            <div className="mx-4 mt-4 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3" data-testid="low-hours-banner">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <b>{behindStaff.length} staff {behindStaff.length === 1 ? 'is' : 'are'} behind</b> on the {directorOverview.staff[0]?.requiredHours}-hour requirement.
              </p>
            </div>
          )}

          <div className="p-4 space-y-3">
            {directorOverview.staff.map(staff => (
              <div key={staff.staffId} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className={`flex items-center justify-between px-4 py-3 cursor-pointer ${staff.isCompliant ? 'bg-green-50' : staff.totalHours > 0 ? 'bg-yellow-50' : 'bg-white'}`}
                  onClick={() => setShowHistory(p => ({...p, [staff.staffId]: !p[staff.staffId]}))}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800 text-sm">{staff.staffName}</span>
                      {staff.isCompliant
                        ? <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Compliant</span>
                        : <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" /> {staff.totalHours}/{staff.requiredHours} hrs</span>}
                      {(() => {
                        const certsNeeded = (staff.entries || []).filter(e => e.hasCertificate === false).length;
                        return certsNeeded > 0 && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full" data-testid={`cert-needed-badge-${staff.staffId}`}>
                            {certsNeeded} certificate{certsNeeded === 1 ? '' : 's'} needed
                          </span>
                        );
                      })()}
                      {isNearingEnd && !staff.isCompliant && staff.totalHours < staff.requiredHours && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`behind-badge-${staff.staffId}`}>
                          <AlertTriangle className="w-3 h-3" /> Behind — needs {(staff.requiredHours - staff.totalHours).toFixed(1)} more hrs
                        </span>
                      )}
                    </div>
                    <div className="mt-1 max-w-xs">{hoursBar(staff.totalHours, staff.requiredHours)}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); printTrainingSummary([staff]); }}
                    title="Print training summary"
                    data-testid={`print-summary-${staff.staffId}`}
                    className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 mr-2 flex-shrink-0">
                    <Printer className="w-4 h-4" />
                  </button>
                  {!staff.isCompliant && (
                    <Popover
                      open={reminderOpen === staff.staffId}
                      onOpenChange={(open) => {
                        setReminderOpen(open ? staff.staffId : null);
                        if (open) setReminderChannels({ email: false, sms: false });
                      }}
                    >
                      <PopoverTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          title="Send reminder"
                          data-testid={`send-reminder-btn-${staff.staffId}`}
                          className="p-2 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 mr-2 flex-shrink-0">
                          <Send className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="end"
                        className="w-64 p-3 space-y-2"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`reminder-popover-${staff.staffId}`}
                      >
                        <p className="text-xs font-semibold text-gray-700">Send reminder to {staff.staffName}</p>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={reminderChannels.email}
                            onChange={(e) => setReminderChannels(prev => ({ ...prev, email: e.target.checked }))}
                            data-testid={`reminder-email-checkbox-${staff.staffId}`}
                          />
                          Email
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={reminderChannels.sms}
                            onChange={(e) => setReminderChannels(prev => ({ ...prev, sms: e.target.checked }))}
                            data-testid={`reminder-sms-checkbox-${staff.staffId}`}
                          />
                          SMS
                        </label>
                        <div className="flex gap-2 pt-1">
                          <Button
                            size="sm"
                            disabled={!reminderChannels.email && !reminderChannels.sms}
                            onClick={() => { setReminderOpen(null); setReminderConfirmStaff(staff); }}
                            className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex-1"
                            data-testid={`reminder-send-confirm-${staff.staffId}`}
                          >
                            Send
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setReminderOpen(null)} className="rounded-lg">
                            Cancel
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                  {showHistory[staff.staffId] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
                {showHistory[staff.staffId] && staff.entries.length > 0 && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50">
                    {staff.entries.map(entry => (
                      <div key={entry.id} className="flex items-center justify-between px-4 py-2 bg-gray-50">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-gray-700 truncate">{entry.trainingName}
                            {entry.entryType === 'director_credit' && <span className="ml-1.5 text-xs bg-blue-100 text-blue-600 px-1.5 rounded-full">Credit</span>}
                            {entry.entryType === 'director_entry' && <span className="ml-1.5 text-xs bg-purple-100 text-purple-600 px-1.5 rounded-full">Staff Record</span>}
                            {entry.entryType === 'auto_clearance' && <span className="ml-1.5 text-xs bg-green-100 text-green-700 px-1.5 rounded-full">Clearance</span>}
                          </p>
                          <p className="text-xs text-gray-400">{entry.hours} hrs · {new Date(entry.completionDate + 'T12:00:00').toLocaleDateString()}{entry.notes ? ` · ${entry.notes}` : ''}</p>
                        </div>
                        <div className="flex gap-1.5 ml-2">
                          {entry.fileName && (<>
                            <button onClick={() => handleViewCert(entry.id)} className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100" title="View"><Printer className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleDownloadCert(entry.id, entry.fileName)} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100" title="Download"><Download className="w-3.5 h-3.5" /></button>
                          </>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {showHistory[staff.staffId] && staff.entries.length === 0 && (
                  <div className="px-4 py-3 bg-gray-50 border-t text-xs text-gray-400 italic">No entries this period</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={!!reminderConfirmStaff} onOpenChange={(open) => { if (!open) setReminderConfirmStaff(null); }}>
        <AlertDialogContent data-testid="reminder-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Send Reminder</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to send a training hours reminder to <strong>{reminderConfirmStaff?.staffName}</strong> via{' '}
              <strong>
                {[reminderChannels.email && 'Email', reminderChannels.sms && 'SMS'].filter(Boolean).join(' and ')}
              </strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reminder-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleSendReminder(reminderConfirmStaff)}
              disabled={reminderSending}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reminder-confirm-yes"
            >
              {reminderSending ? "Sending..." : "Yes, Send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProfessionalDevelopmentHub;
